import type { PageProps } from '../app';
import { navigate } from '../router';
import { EntriesTable } from '../ui/components/EntriesTable';
import { LogLayout } from '../ui/components/LogLayout';
import { Spinner } from '../ui/components/Spinner';
import { ErrorBox } from '../ui/components/ErrorBox';
import { parseIndex } from '../ui/format';
import { useLogClient } from '../ui/hooks/useLogClient';
import { useAsync } from '../ui/hooks/useAsync';
import { refreshTreeHead, treeHeads } from '../ui/state/treeHeads';
import { LoadingPage, NotFoundLog } from './Log';
import { logRoute } from '../ui/nav';

/** Entries tab: one page of entries with a jump box. */
export function EntriesPage({ params, query }: PageProps) {
  const { log, client, loading } = useLogClient(params);
  const th = log ? treeHeads.value[log.id] : undefined;
  const head = useAsync(async () => (client ? refreshTreeHead(client) : undefined), [client]);
  if (loading) return <LoadingPage />;
  if (!log) return <NotFoundLog id={`${params.op}/${params.log}`} />;
  const start = parseIndex(query.get('start') ?? '0') ?? 0;
  const go = (s: number) => navigate(logRoute(log, 'entries'), { start: s });
  return (
    <LogLayout log={log} {...(client ? { client } : {})} tab="entries" trail={[{ label: 'Entries' }]}>
      {client && (
        <>
          {head.status === 'error' && <ErrorBox error={head.error} title="Could not fetch the tree head" />}
          {!th && head.status === 'loading' && <Spinner label="Fetching the tree head…" />}
          {th && (
            <div class="stack">
              <EntriesTable client={client} treeSize={th.treeSize} start={Math.min(start, Math.max(0, th.treeSize - 1))} onNavigate={go} />
            </div>
          )}
        </>
      )}
    </LogLayout>
  );
}
